import {
  Transaction,
  TransactionType,
  MechanismType,
} from "@/domain/enrollment.interface";
import { Reward } from "@/domain/enrollment.interface";
import dayjs from "dayjs";

/**
 * Get transaction event label based on transaction type and mechanism
 * - EARNED transactions (all mechanisms) = "Validation successful"
 * - Redeemed items = "Giftcard generated"
 */
export const getTransactionEventLabel = (
  transaction: Transaction | Reward,
  isRedeemedItem = false,
): string => {
  if (isRedeemedItem) {
    return "Giftcard generated";
  }

  // For regular transactions, EARNED types are "Validation successful"
  if ("type" in transaction && transaction.type === TransactionType.EARNED) {
    if (transaction.mechanism === "INCENTIVE") {
      return "On demand Incentive";
    } else {
      return "Validation successful";
    }
  }

  // Fallback for other transaction types
  if ("type" in transaction) {
    switch (transaction.type) {
      case TransactionType.REDEEMED:
        return "Giftcard generated";
      case TransactionType.REFUNDED:
        return "Reimbursement";
      default:
        return "Transaction";
    }
  }

  return "Transaction";
};

/**
 * Format transaction amount with currency symbol and +/- sign
 * Positive amounts get no sign, negative amounts get minus sign
 */
export const formatTransactionAmount = (
  amount: number,
  currencySymbol: string,
): string => {
  const absAmount = Math.abs(amount);
  const formattedAmount = absAmount.toFixed(2);

  if (amount < 0) {
    return `-${currencySymbol}${formattedAmount}`;
  }

  return `${currencySymbol}${formattedAmount}`;
};

/**
 * Format transaction timestamp to DD-MMM-YYYY, hh:MM AM/PM
 */
export const formatTransactionTimestamp = (date: string | Date): string => {
  return dayjs(date).format("DD-MMM-YYYY, hh:mm A");
};

/**
 * Combined transaction type for display
 */
export interface DisplayTransaction {
  id: string;
  timestamp: Date;
  amount: number;
  event: string;
  originalDate: string; // Keep original for sorting
}

/**
 * Transform enrollment transactions to display format
 */
export const transformEnrollmentTransaction = (
  transaction: Transaction,
): DisplayTransaction => {
  return {
    id: transaction._id,
    timestamp: new Date(transaction.date),
    amount: transaction.amount,
    event: getTransactionEventLabel(transaction, false),
    originalDate: transaction.date,
  };
};

/**
 * Transform redeemed item to display format (negative amount)
 */
export const transformRedeemedItem = (reward: Reward): DisplayTransaction => {
  return {
    id: reward._id || `reward-${reward.date}`,
    timestamp: new Date(reward.date),
    amount: -Math.abs(reward.amount), // Ensure negative
    event: getTransactionEventLabel(reward, true),
    originalDate: reward.date,
  };
};
